// Authenticated calls to the Arlo server's JSON endpoints.

import { devicePrefs } from './device.ts';

export class ApiError extends Error {
  readonly status: number;
  constructor(message: string, status: number) {
    super(message);
    this.status = status;
  }
}

export function apiUrl(path: string): string {
  return `${devicePrefs().serverUrl}${path}`;
}

/** Calls where Claude thinks or speech is made: these can take a while even on good Wi-Fi. */
const SLOW = /^\/api\/(ask|parse|brief|summary|report|health\/card|shopping\/(advice|ideas)|sonos\/ask|stt|tts)\b/;

export async function api<T>(path: string, init: { method?: string; body?: unknown; signal?: AbortSignal; timeoutMs?: number } = {}): Promise<T> {
  const d = devicePrefs();
  // Never hang on a dead connection: a spinner that never ends feels broken.
  const timeout = AbortSignal.timeout(init.timeoutMs ?? (SLOW.test(path) ? 120_000 : 20_000));
  let res: Response;
  try {
    res = await fetch(apiUrl(path), {
      method: init.method ?? (init.body === undefined ? 'GET' : 'POST'),
      headers: { ...(init.body === undefined ? {} : { 'Content-Type': 'application/json' }), ...(d.key ? { Authorization: `Bearer ${d.key}` } : {}) },
      body: init.body === undefined ? undefined : JSON.stringify(init.body),
      signal: init.signal && 'any' in AbortSignal ? AbortSignal.any([init.signal, timeout]) : (init.signal ?? timeout),
    });
  } catch (e) {
    if (timeout.aborted && !init.signal?.aborted) throw new ApiError('The connection is too weak right now — try again in a moment.', 0);
    if (e instanceof TypeError) throw new ApiError('Can’t reach Arlo HQ — check your signal and try again.', 0);
    throw e;
  }
  const json = (await res.json().catch(() => ({}))) as T & { error?: string };
  if (!res.ok) throw new ApiError(json.error ?? `Error ${res.status}`, res.status);
  return json;
}

export type KeyCheck = 'owner' | 'guest' | 'wrong' | 'no-server';

/** Try a household key against the server before relying on it. */
export async function checkKey(key: string, serverUrl = devicePrefs().serverUrl): Promise<KeyCheck> {
  try {
    const res = await fetch(`${serverUrl}/api/whoami`, { headers: { Authorization: `Bearer ${key.trim()}` } });
    if (res.status === 401 || res.status === 403) return 'wrong';
    if (!res.ok) return 'no-server';
    const json = (await res.json().catch(() => ({}))) as { role?: string };
    return json.role === 'guest' ? 'guest' : json.role === 'owner' ? 'owner' : 'no-server';
  } catch {
    return 'no-server';
  }
}
